import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn-ui/table';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Reusable DataTable component with pagination and search
 * @param {Object} props
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.columns - Array of column definitions { key, label, render?, className? }
 * @param {number} props.itemsPerPage - Items per page (default: 10, only for local pagination)
 * @param {boolean} props.searchable - Enable search functionality
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {Function} props.onRowClick - Callback when row is clicked
 * @param {string} props.emptyMessage - Message when no data
 * @param {Object} props.pagination - External pagination object { total, page, limit, totalPages }
 * @param {Function} props.onPageChange - Callback when page changes (for external pagination)
 * @param {boolean} props.loading - Loading state
 */
export const DataTable = ({
  data = [],
  columns = [],
  itemsPerPage = 10,
  searchable = false,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data available',
  className,
  pagination: externalPagination,
  onPageChange,
  loading = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Use external pagination if provided, otherwise use local pagination
  const isExternalPagination = !!externalPagination && !!onPageChange;

  // Filter data based on search (only for local pagination)
  const filteredData = searchable && !isExternalPagination
    ? data.filter(item =>
        columns.some(col => {
          const value = item[col.key];
          return value?.toString().toLowerCase().includes(searchQuery.toLowerCase());
        })
      )
    : data;

  // Calculate pagination
  const totalPages = isExternalPagination ? externalPagination.totalPages : Math.ceil(filteredData.length / itemsPerPage);
  const currentPageNumber = isExternalPagination ? externalPagination.page : currentPage;
  const startIndex = isExternalPagination ? (externalPagination.page - 1) * externalPagination.limit : (currentPage - 1) * itemsPerPage;
  const endIndex = isExternalPagination ? startIndex + externalPagination.limit : startIndex + itemsPerPage;
  const totalItems = isExternalPagination ? externalPagination.total : filteredData.length;
  const paginatedData = isExternalPagination ? data : filteredData.slice(startIndex, endIndex);

  // Reset to first page when search changes
  React.useEffect(() => {
    if (!isExternalPagination) {
      setCurrentPage(1);
    }
  }, [searchQuery, isExternalPagination]);

  const handlePrevious = () => {
    if (isExternalPagination) {
      onPageChange(currentPageNumber - 1);
    } else {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }
  };

  const handleNext = () => {
    if (isExternalPagination) {
      onPageChange(currentPageNumber + 1);
    } else {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search */}
      {searchable && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(col => (
                <TableHead key={col.key} className={col.headerClassName}>
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  onClick={() => onRowClick?.(row)}
                  className={onRowClick ? 'cursor-pointer' : ''}
                >
                  {columns.map(col => (
                    <TableCell key={col.key} className={col.cellClassName}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentPageNumber === 1 || loading}>
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm">
              Page {currentPageNumber} of {totalPages}
            </div>
            <Button variant="outline" size="sm" onClick={handleNext} disabled={currentPageNumber === totalPages || loading}>
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
